/*
 * Design note for this page:
 * Cybernetic Editorial Observatory applied to legal disclaimers.
 * Keep the tone serious, precise, and commercially protective, with clear boundaries
 * around public-figure references, estimates, and proprietary automated analysis.
 */

import PolicyLayout from "@/components/PolicyLayout";
import { BrainCircuit, Landmark, ShieldAlert } from "lucide-react";

const cards = [
  {
    title: "No endorsement",
    body: "References to public figures, well-known personalities, or public datasets are illustrative only and do not imply endorsement, sponsorship, partnership, or approval by those individuals or their representatives.",
    icon: ShieldAlert,
  },
  {
    title: "Individualized automation",
    body: "Command Secrets is presented as an automated decision-intelligence system that generates individualized outputs from user-specific inputs and logged behaviour, not from generic group averages alone.",
    icon: BrainCircuit,
  },
  {
    title: "Boundary control",
    body: "The website explains what the system does and what users should not assume. It does not publish full proprietary formulas, full weighting logic, or guarantees of future outcomes.",
    icon: Landmark,
  },
];

const sections = [
  {
    id: "public-figures",
    title: "Public figure and reference-data disclaimer",
    intro:
      "Command Secrets may discuss famous public figures, historical personalities, entrepreneurs, or publicly discussed life events as illustrative reference cases only.",
    paragraphs: [
      "Any such reference is provided for educational, comparative, editorial, or explanatory purposes. It is not presented as a claim of endorsement, a private collaboration, a licensed biography, or a verified personal assessment commissioned by the referenced person.",
      "Where public names, biographies, birthdays, careers, or publicly circulated facts are mentioned, Command Secrets relies on information that is already publicly available or commonly reported. Users should not interpret such references as proof that the system has a direct relationship with, privileged access to, or special authority over any public figure.",
      "If a public figure, representative, or rights holder requests correction, clarification, or removal of a specific reference, Command Secrets may review the request and update or remove the relevant content where appropriate.",
    ],
  },
  {
    id: "net-worth",
    title: "Estimated net worth and financial-position disclaimer",
    intro:
      "Any mention of estimated wealth, commercial standing, or financial direction is inherently uncertain and should be treated as an estimate, commentary, or scenario-based interpretation rather than verified fact.",
    paragraphs: [
      "Net worth figures that appear in examples, reference materials, or explanatory outputs may depend on public reporting, inferred asset assumptions, timing, market movements, business ownership uncertainty, exchange-rate changes, debt visibility, and incomplete public disclosures. They are not audited financial statements.",
      "Command Secrets does not guarantee the accuracy, completeness, or present-day correctness of any public net worth estimate. Users must not rely on website examples or analytical outputs as investment advice, financial advice, credit advice, legal advice, tax advice, or a substitute for licensed professional consultation.",
      "Where the system produces individualized commentary, that output should still be read as an analytical aid, not as a promise of future earnings, business success, valuation performance, or loss prevention.",
    ],
  },
  {
    id: "ai-analysis",
    title: "Automated individualized analysis disclaimer",
    intro:
      "Command Secrets positions its calculations as automated and individualized, but every output remains subject to input quality, context limits, and the boundaries of interpretive systems.",
    paragraphs: [
      "The website and application describe a proprietary analytical framework that uses user-provided details, behavioural logs, and structured interpretation layers to generate individualized signals about strengths, drivers, blind spots, fit, and decision patterns. That does not mean the output is infallible, exhaustive, or suitable for every high-stakes decision without human judgment.",
      "Users remain responsible for verifying key decisions involving health, law, investment, regulated employment, insurance, immigration, family matters, or any other area where a professional adviser or licensed practitioner should be consulted.",
      "Command Secrets may improve its models, scoring surfaces, wording, thresholds, or product logic over time. Past outputs, sample screens, and previous reports may not exactly match later versions of the system.",
    ],
  },
  {
    id: "ip-boundary",
    title: "Protected methodology and report-use boundary",
    intro:
      "The public website explains the value of the system without exposing the protected calculation order, weighting architecture, or full internal framework.",
    paragraphs: [
      "Website descriptions, app copy, examples, screenshots, and PDF reports are provided subject to intellectual-property protection. Users may not copy, resell, reverse engineer, systematically scrape, clone, republish, or misrepresent proprietary COMMAND Secrets outputs as their own methodology or product.",
      "Where downloadable reports are provided, watermarking, branded notices, access controls, and anti-copy measures may be used to preserve authorship, commercial value, and chain-of-custody integrity for the analysis surface.",
      "If you need clarification about permitted use, billing, or content removal, please use the public support page so the request can be reviewed through the formal customer-service path.",
    ],
  },
];

const relatedLinks = [
  {
    label: "Privacy notice",
    href: "/legal/privacy",
    description: "Read how Command Secrets handles personal data, privacy requests, retention, and cross-jurisdiction commitments.",
  },
  {
    label: "Cancellation terms",
    href: "/legal/cancellation",
    description: "Review how cancellations, plan changes, and no-refund rules apply once digital analysis work has been rendered.",
  },
  {
    label: "Support",
    href: "/support",
    description: "Use the manual support path for billing questions, upgrade or downgrade requests, and customer-service contact handling.",
  },
  {
    label: "Security",
    href: "/security",
    description: "See the public security posture, access-control expectations, and anti-abuse recommendations for the service surface.",
  },
];

export default function Disclaimer() {
  return (
    <PolicyLayout
      eyebrow="Legal disclaimer"
      title="Illustrative references stay separate from endorsement, audited fact, and guaranteed outcomes."
      intro="This page explains the legal and editorial boundaries around public-figure references, estimated net worth examples, individualized AI-generated interpretation, and protected COMMAND Secrets methodology. It is written so customers, reviewers, and rights holders can see what the system claims, and what it deliberately does not claim."
      highlight="Clear legal boundaries protect both the user and the proprietary system surface."
      cards={cards}
      sections={sections}
      relatedLinks={relatedLinks}
    />
  );
}
